import React from 'react';
import styled, { keyframes } from 'styled-components';
import Layout from '../../components/Layout';
import Footer from '../../components/Footer';
import { FaTelegram, FaClock, FaGlobe } from 'react-icons/fa';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 6rem 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 1rem 2rem 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #fff;
  text-shadow: 0 0 16px #6ea8ff, 0 2px 10px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  background: rgba(20, 30, 60, 0.7);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(45, 108, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 20px 40px rgba(45, 108, 255, 0.2),
      0 8px 16px rgba(45, 108, 255, 0.1);
    border-color: rgba(45, 108, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1.2rem;
    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const SectionTitle = styled.div`
  padding: 1.5rem;
  font-weight: 700;
  font-size: 1.15rem;
  color: #fff;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.2) 0%, rgba(58, 143, 255, 0.1) 100%);
  border-bottom: 1px solid rgba(45, 108, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
  color: #b6d0ff;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    color: #6ea8ff;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      text-decoration: underline;
      color: #3a8fff;
    }
  }
  
  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const SupportCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(30, 42, 74, 0.6);
  backdrop-filter: blur(8px);
  border: 2px solid rgba(45, 108, 255, 0.15);
  border-radius: 12px;
  margin-bottom: 1rem;
  text-decoration: none;
  color: #fff;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    border-color: rgba(45, 108, 255, 0.4);
    box-shadow: 0 8px 24px rgba(45, 108, 255, 0.3);
    background: rgba(30, 42, 74, 0.8);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IconWrapper = styled.div`
  font-size: 2rem;
  color: #6ea8ff;
  filter: drop-shadow(0 4px 8px rgba(45, 108, 255, 0.4));
  transition: transform 0.3s ease;
  
  ${SupportCard}:hover & {
    transform: scale(1.1);
  }
`;

const SupportInfo = styled.div`
  flex: 1;
`;

const SupportTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.3rem;
  color: #fff;
  transition: color 0.3s ease;
  
  ${SupportCard}:hover & {
    color: #b6d0ff;
  }
`;

const SupportDescription = styled.p`
  font-size: 1rem;
  color: #b6d0ff;
  margin: 0;
  opacity: 0.9;
  transition: opacity 0.3s ease;
  
  ${SupportCard}:hover & {
    opacity: 1;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #b6d0ff;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    color: #6ea8ff;
  }
`;

const ContactSupport = () => {
  return (
    <Layout>
      <PageWrapper>
        <BackgroundPattern />
        <Container>
          <PageTitle>Contact Support</PageTitle>
          
          <Section>
            <SectionTitle>Telegram Support</SectionTitle>
            <SectionContent>
              <p>Our support team is available on Telegram. Choose the appropriate contact based on your needs:</p>
              
              <SupportCard href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                <IconWrapper>
                  <FaTelegram />
                </IconWrapper>
                <SupportInfo>
                  <SupportTitle>@deserved0</SupportTitle>
                  <SupportDescription>Owner/Developer - Contact for payment issues, account problems, and bug reports</SupportDescription>
                </SupportInfo>
              </SupportCard>
              
              <SupportCard href="https://t.me/tomnookbank" target="_blank" rel="noopener noreferrer">
                <IconWrapper>
                  <FaTelegram />
                </IconWrapper>
                <SupportInfo>
                  <SupportTitle>@tomnookbank</SupportTitle>
                  <SupportDescription>Primary support contact for technical assistance and general inquiries</SupportDescription>
                </SupportInfo>
              </SupportCard>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Support Information</SectionTitle>
            <SectionContent>
              <InfoRow>
                <FaClock />
                <span>Response Time: Usually within 1-2 hours</span>
              </InfoRow>
              <InfoRow>
                <FaGlobe />
                <span>Support Hours: 24/7</span>
              </InfoRow>
              <p>When contacting support, please include:</p>
              <ul>
                <li>Your Account Warmer version</li>
                <li>Operating system details</li>
                <li>Description of the issue</li>
                <li>Any error messages you're seeing</li>
                <li>Steps to reproduce the problem</li>
              </ul>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>Before Contacting Support</SectionTitle>
            <SectionContent>
              <p>To help us assist you better, please check these resources first:</p>
              <ul>
                <li><a href="/docs/troubleshooting">Troubleshooting Guide</a> - Solutions for common issues</li>
                <li><a href="/docs/how-it-works">How It Works</a> - Understanding Account Warmer's functionality</li>
                <li><a href="/faq">FAQ</a> - Answers to frequently asked questions</li>
              </ul>
            </SectionContent>
          </Section>
        </Container>
        <Footer />
      </PageWrapper>
    </Layout>
  );
};

export default ContactSupport; 